import { useTranslation } from 'react-i18next'
import { useDialog } from '@/components/common/dialog'
import { Button, ErrorAlert, InfoDialog } from '@repo/ui'
import { DoneIcon32 } from '@repo/ui/icons'
import SimpleApplyCancelDialog from '@/components/common/dialog/SimpleApplyCancelDialog'
import { BfnStoreDataEntry } from '@/store/useBfnStore'
import useBfnErledigenMutation from '@/features/befehlsnachrichten/mutations/useBfnErledigenMutation'
import useOnlineDetection from '@/features/offline/hooks/useOnlineDetection'

export interface BfnErledigenActionProps {
  data: BfnStoreDataEntry
  isBlocked: boolean
  'data-testid': string
}

export default function BfnErledigenAction({
  data,
  isBlocked,
  'data-testid': testId
}: Readonly<BfnErledigenActionProps>) {
  const { t } = useTranslation('bfnDetails')
  const [isErledigenDialogOpen, openErledigenDialog, closeErledigenDialog] =
    useDialog()
  const [isBlockedDialogOpen, openBlockedDialog, closeBlockedDialog] =
    useDialog()

  const isOnline = useOnlineDetection()

  const { mutation } = useBfnErledigenMutation(data.abruftoken)

  const onApply = () => {
    mutation.mutate(undefined, {
      onSuccess: () => {
        closeErledigenDialog()
      }
    })
  }

  const handleClick = () => {
    if (isBlocked) {
      openBlockedDialog()
    } else {
      openErledigenDialog()
    }
  }

  return (
    <>
      <SimpleApplyCancelDialog
        title={t('dialog.erledigen.title')}
        state={isErledigenDialogOpen}
        onApply={() => onApply()}
        onCancel={closeErledigenDialog}
        applyButtonDisabled={mutation.isPending}
        applyButtonText={t('dialog.erledigen.apply')}
        cancelButtonText={t('dialog.erledigen.cancel')}
        data-testid={`${testId}-dialog`}
      >
        <p data-testid={`${testId}-text`}>{t('dialog.erledigen.text')}</p>
        {mutation.isError && (
          <ErrorAlert error={mutation.error} data-testid={`${testId}-error`}>
            {t('dialog.erledigen.error')}
          </ErrorAlert>
        )}
      </SimpleApplyCancelDialog>

      <InfoDialog
        title={t('dialog.erledigen.action_blocked_dialog.title')}
        state={isBlockedDialogOpen}
        onOk={closeBlockedDialog}
        onClose={closeBlockedDialog}
        okButtonText={t('dialog.erledigen.action_blocked_dialog.ok')}
        data-testid={`${testId}-blocked-dialog`}
      >
        <p data-testid={`${testId}-blocked-text`}>
          {t('dialog.erledigen.action_blocked_dialog.text')}
        </p>
      </InfoDialog>

      <Button
        variant="brand-primary"
        type="button"
        onClick={() => handleClick()}
        data-testid={`${testId}-button`}
        disabled={!isOnline}
      >
        <DoneIcon32
          className="shrink-0"
          data-testid={`${testId}-button-icon`}
        />
        <span className="pl-0.5" data-testid={`${testId}-button-text`}>
          {t('actions.erledigt_button')}
        </span>
      </Button>
    </>
  )
}
